import { useState } from 'react';
import { getBackendUrl } from '../utils/imageUtils';

interface UseImageUploadProps {
  entityId: string;
  cardType: 'characters' | 'elements' | 'pillars';
  entityName: string;
  onUpdate: (updatedEntity: any) => void;
  setImageLoading: (loading: boolean) => void;
  setImageKey: (key: number) => void;
  setUploading: (uploading: boolean) => void;
  showToast?: (message: string, type?: 'success' | 'error' | 'info' | 'warning') => void;
}

interface UseImageUploadReturn {
  handleImageUpload: (file: File) => Promise<void>;
  uploading: boolean;
}

export const useImageUpload = ({
  entityId,
  cardType,
  entityName,
  onUpdate,
  setImageLoading,
  setImageKey,
  setUploading,
  showToast
}: UseImageUploadProps): UseImageUploadReturn => {
  const [uploading, setUploadingState] = useState(false);

  const handleImageUpload = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      if (showToast) {
        showToast('Please select an image file.', 'error');
      }
      return;
    }

    try {
      setUploadingState(true);
      setUploading(true);
      setImageLoading(true);
      
      // Create FormData
      const formData = new FormData();
      formData.append('image', file);
      formData.append('cardType', cardType);
      
      const token = localStorage.getItem('token');
      const headers: Record<string, string> = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${getBackendUrl()}/${cardType}/${entityId}/image`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
        headers
      });
      
      if (!response.ok) {
        throw new Error(`Upload failed: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('✅ Upload successful:', data);
      
      // Extract the new image URL from the correct response path
      const newImageUrl = data.data?.imageUrl || data.data?.[cardType.slice(0, -1)]?.imageUrl;
      const uploadTimestamp = data.data?.uploadTimestamp || Date.now();
      const newImage = data.data?.[cardType.slice(0, -1)]?.image;
      
      console.log('🔄 Extracted data:', { newImageUrl, uploadTimestamp, newImage });
      
      if (newImageUrl) {
        console.log('🔄 Updating entity with new image URL:', newImageUrl);
        
        // Force image reload by generating new key
        const newKey = Date.now();
        console.log('🔄 Generating new image key:', newKey);
        setImageKey(newKey);
        
        // Create updated entity with new image data
        const updatedEntity = { 
          imageUrl: newImageUrl,
          image: newImage, // Also update the image key
          uploadTimestamp: uploadTimestamp // Include upload timestamp
        };
        
        // Store the timestamp in localStorage for future reference
        localStorage.setItem(`${cardType.slice(0, -1)}_${entityId}_timestamp`, uploadTimestamp.toString());
        
        // Update parent component
        onUpdate(updatedEntity);
        
        // Force a small delay to ensure state updates are processed
        setTimeout(() => {
          setImageLoading(false);
          console.log('🔄 Image refresh completed');
        }, 100);
        
        // Special handling for L1 pillars
        if (cardType === 'pillars') {
          const L1PillarCategories = [
            'Events and History', 
            'Geography and Places', 
            'Organizations and Societies',
            'Culture and Beliefs',
            'Technology and Creations',
            'Flora and Fauna',
            'Magic and Abilities',
            'Races and Species'
          ];
          
          // Check if this is an L1 pillar (would need entity name passed in)
          const isL1Pillar = L1PillarCategories.includes(entityName);
          if (isL1Pillar && showToast) {
            showToast(`L1 pillar image updated! All child pillars of "${entityName}" have been automatically updated.`, 'success');
          }
        }
        
      } else {
        console.error('❌ No image URL in server response');
        console.error('❌ Response structure:', data);
        setImageLoading(false);
      }
      
      if (showToast) {
        showToast('Image updated successfully!', 'success');
      }
      
    } catch (error) {
      console.error('❌ Upload failed:', error);
      setImageLoading(false);
      if (showToast) {
        showToast('Failed to update image. Please try again.', 'error');
      }
      throw error; // Re-throw for modal to handle
    } finally {
      setUploadingState(false);
      setUploading(false);
    }
  };

  return {
    handleImageUpload,
    uploading: uploading || false
  };
}; 